import React from "react";
import { DataTable, Column } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, User } from "lucide-react";
import { Team } from "@/services/teamService";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { useNavigate } from "react-router-dom";
import TruncatedText from "@/components/common/TruncatedText";
import { useTranslation } from "react-i18next";

interface TeamTableProps {
  data: Team[];
  isLoading: boolean;
  onDelete: (id: string) => void;
  onStatusToggle: (team: Team) => void;
  sortBy: string | null;
  sortOrder: "asc" | "desc" | null;
  onSort: (field: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onSiteStaffIds: Set<string>;
}

export const TeamTable: React.FC<TeamTableProps> = ({
  data,
  isLoading,
  onDelete,
  onStatusToggle,
  sortBy,
  sortOrder,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
  onSiteStaffIds,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const columns: Column<Team>[] = [
    {
      key: "isActive",
      header: "Status",
      sortable: true,
      render: (_, team) => (
        <Switch
          checked={team.isActive}
          onCheckedChange={() => onStatusToggle(team)}
        />
      ),
    },
    {
      key: "name",
      header: "Group name",
      className: "font-medium",
      sortable: true,
      render: (_, team) => (
        <TruncatedText limit={30}>{team.name}</TruncatedText>
      ),
    },
    {
      key: "users",
      header: "Users",
      sortable: true,
      render: (_, team) => {
        const count = team._count?.userTeams || 0;
        if (count === 1 && team.userTeams?.[0]) {
          const user = team.userTeams[0].staffUser;
          const profile = user.profile;
          return (
            <div className="flex items-center gap-2">
              <PresignedImageWithPlaceholder
                className="h-8 w-8 rounded-full object-cover"
                photo={profile?.photoId}
                alt={`${profile?.firstName} ${profile?.lastName}`}
                placeholder={
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      <User className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                }
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {profile?.firstName} {profile?.lastName}
                </span>
                <span className="text-muted-foreground text-xs">
                  {user.position || "Staff"}
                </span>
              </div>
            </div>
          );
        }
        return <span>{count} users</span>;
      },
    },
    {
      key: "onSite",
      header: t("staff.table.onSite"),
      className: "text-center",
      render: (_, team) => {
        const count =
          team.userTeams?.filter(
            ({ staffUser }) =>
              onSiteStaffIds.has(staffUser.id) ||
              onSiteStaffIds.has(staffUser.profileId),
          ).length ?? 0;

        return (
          <div className="flex items-center justify-center gap-1.5">
            <span className="size-2 rounded-full bg-green-500" />
            <span>{count}</span>
          </div>
        );
      },
    },
    {
      key: "responsibleStaff",
      header: "Responsible Staff",
      sortable: true,
      render: (_, team) => {
        const responsibleStaff = team.responsibleStaff;
        if (!responsibleStaff) return <span>-</span>;

        const profile = responsibleStaff.profile;
        return (
          <div className="flex items-center gap-2">
            <PresignedImageWithPlaceholder
              className="h-8 w-8 rounded-full object-cover"
              photo={profile?.photoId}
              alt={`${profile?.firstName} ${profile?.lastName}`}
              placeholder={
                <Avatar className="h-8 w-8">
                  <AvatarFallback>
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
              }
            />
            <div className="flex flex-col">
              <span className="text-sm font-medium">
                {profile?.firstName} {profile?.lastName}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      render: (_, team) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/staff/teams/${team.id}`)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(team.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      loading={isLoading}
      withPagination={true}
      withSorting={true}
      sorting={{
        field: sortBy,
        direction: sortOrder || null,
        onSort: onSort,
      }}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
      }}
      emptyMessage="No teams found"
    />
  );
};
